<template>
  <Sheet v-model:open="isOpen">
    <SheetContent class="overflow-y-auto">
      <SheetHeader padded>
        <SheetTitle size="lg">Edit Facebook Advertising Configuration</SheetTitle>
      </SheetHeader>

      <Separator />

      <SheetBody padded class="flex flex-col gap-3">
        <div>
          <Label class="text-sm font-medium text-gray-700">Status</Label>
          <Select v-model="form.status">
            <SelectTrigger class="mt-1 w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="not_started">Not Started</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label class="text-sm font-medium text-gray-700">Form ID</Label>
          <Input v-model="form.form_id" class="mt-1" placeholder="Form ID" />
        </div>

        <div>
          <Label class="text-sm font-medium text-gray-700">Error</Label>
          <Input v-model="form.error" class="mt-1" placeholder="Error message" />
        </div>

        <Button class="mt-6" :disabled="isSaving" @click="handleSave">
          {{ isSaving ? 'Saving...' : 'Save' }}
        </Button>
      </SheetBody>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetBody } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { hFetch } from '@/lib/hfetch';
import { toast } from 'vue-sonner';
import type { FacebookAdvertisingConfig } from './types';

type Props = {
  open: boolean;
  config: FacebookAdvertisingConfig;
  teamId: string;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:open': [value: boolean];
  saved: [];
}>();

const isOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit('update:open', value),
});

const isSaving = ref(false);

const form = ref({
  status: props.config.status || 'inactive',
  form_id: props.config.form_id || '',
  error: props.config.error || '',
});

const handleSave = async () => {
  isSaving.value = true;
  try {
    const result = await hFetch<FacebookAdvertisingConfig, unknown>({
      url: `/api/admin/teams/${props.teamId}/facebook_advertising_configuration`,
      method: 'PUT',
      payload: { facebook_advertising_configuration: form.value },
    });
    if (result.status === 'success') {
      toast.success('Facebook Advertising Configuration updated');
      isOpen.value = false;
      emit('saved');
    } else {
      toast.error('Update failed', { description: result.messages.join(', ') });
    }
  } catch {
    toast.error('An error occurred while saving');
  } finally {
    isSaving.value = false;
  }
};
</script>
